import { useState } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { usePortalAuth, useNoIndex, extractErr, api } from "@/portal/PortalAuth";

const inputCls = "w-full rounded-lg bg-white border border-[#E7E5E4] focus:outline-none focus:ring-1 focus:ring-[#8A9A86] focus:border-[#8A9A86] h-12 px-4 text-[#1C1917]";

export default function PortalLogin({ title, redirectTo, allowedRoles, testidPrefix, magicLink = false }) {
  useNoIndex();
  const { user, login } = usePortalAuth();
  const [linkSent, setLinkSent] = useState("");
  const [linkBusy, setLinkBusy] = useState(false);
  const sendLink = async () => {
    if (!email) { setErr("Enter your email first, then request a sign-in link."); return; }
    setLinkBusy(true); setErr("");
    try {
      await api.post("/auth/magic/request", { email });
      setLinkSent(`If an account exists for ${email}, a secure one-time sign-in link is on its way (valid 48 hours).`);
    } catch (e) { setErr(extractErr(e)); } finally { setLinkBusy(false); }
  };
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const location = useLocation();

  if (user && allowedRoles.includes(user.role)) {
    return <Navigate to={redirectTo} state={location.state} replace />;
  }
  if (user && !allowedRoles.includes(user.role)) {
    return <Navigate to="/portal" replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const u = await login(email.trim().toLowerCase(), password);
      if (!allowedRoles.includes(u.role)) {
        setErr(`This account is a ${u.role}. Please use the correct portal.`);
      }
    } catch (e) { setErr(extractErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <section data-testid={`${testidPrefix}-login`} className="px-6 md:px-12 pt-36 md:pt-40 pb-24 min-h-[80vh]">
      <div className="max-w-md mx-auto">
        <Link to="/portal" className="inline-flex items-center gap-2 text-sm text-[#44403C] hover:text-[#1C1917]">
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> All portals
        </Link>
        <div className="mt-8 rounded-2xl border border-[#E7E5E4] bg-[#F5F5F4] p-8 md:p-10">
          <h1 className="text-3xl tracking-tight font-light text-[#1C1917]">{title}</h1>
          <p className="mt-3 text-[#44403C] font-light">{magicLink ? "Enter your email and we'll send a secure one-time sign-in link — or use your password if you set one." : "Sign in with the credentials Masaya Hospitality provided."}</p>
          <form onSubmit={submit} className="mt-8 space-y-4" data-testid={`${testidPrefix}-form`}>
            <div>
              <label className="text-sm text-[#1C1917] mb-1 block">Email</label>
              <input type="email" required data-testid={`${testidPrefix}-email`} value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className="text-sm text-[#1C1917] mb-1 block">Password</label>
              <input type="password" required={!magicLink} data-testid={`${testidPrefix}-password`} value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} />
            </div>
            {err && <p className="text-sm text-red-600" data-testid={`${testidPrefix}-error`}>{err}</p>}
            <button type="submit" disabled={busy} data-testid={`${testidPrefix}-submit`} className="w-full rounded-full bg-[#1C1917] text-white h-12 font-medium text-sm hover:bg-[#44403C] transition-colors disabled:opacity-60">
              {busy ? "Signing in…" : magicLink ? "Sign in with password" : "Sign in"}
            </button>
            {magicLink && (
              <div className="pt-2 text-center">
                <button type="button" onClick={sendLink} disabled={linkBusy} data-testid={`${testidPrefix}-magic-link`} className="text-sm underline underline-offset-4 text-[#1C1917] disabled:opacity-60">
                  {linkBusy ? "Sending…" : "Email me a secure sign-in link instead"}
                </button>
                {linkSent && <p data-testid={`${testidPrefix}-magic-sent`} className="mt-3 text-sm text-[#4A6A48]">{linkSent}</p>}
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}
